import { FloatingNode, useFloatingParentNodeId, FloatingTree } from '@floating-ui/react';
import React__default from 'react';
import '../../utils/index.js';
import '../box/index.js';
import '../modal/index.js';
import { ModalContent } from '../modal/modal_content.js';
import { ModalContext } from '../modal/modal_context.js';
import { useModal } from '../modal/use_modal.js';
import '../paragraph/index.js';
import '../stack/index.js';
import { useCachedProp } from '../../utils/cached_prop.js';
import { Box } from '../box/box.js';
import { Stack } from '../stack/stack.js';
import { ModalHeading } from '../modal/modal_heading.js';
import { Paragraph } from '../paragraph/paragraph.js';

const AlertModalInner = ({ title, description: propDescription, children, trigger, open, onClose, className, ...rest }) => {
    const onToggle = React__default.useCallback((nextOpen) => {
        if (!nextOpen) {
            onClose?.();
        }
    }, [onClose]);
    const state = useModal({
        open,
        onToggle,
        loading: false,
        closeOnOutsidePress: false,
        size: 'small',
        role: 'alertdialog',
        eventComponentName: 'alert-modal',
        referenceElement: trigger,
    });
    const descriptionId = `${state.id}-description`;
    // Cache description to a ref when the AlertModal is open.
    // The cached description is rendered when open is false, i.e. during the out transition.
    // This is necessary, if the open state is controlled from outside,
    // and the description is removed or changed before the out transition has completed.
    const description = useCachedProp({ prop: propDescription, shouldUpdate: state.open });
    return (React__default.createElement(FloatingNode, { id: state.nodeId },
        React__default.createElement(ModalContext.Provider, { value: state },
            React__default.createElement(ModalContent, { ...rest, "aria-describedby": descriptionId, className: className },
                React__default.createElement(Box, { padding: "medium" },
                    React__default.createElement(Stack, { gap: "medium", direction: "column", wrap: false },
                        React__default.createElement(ModalHeading, null, title),
                        React__default.createElement(Stack, { gap: "medium", direction: "column", wrap: false, id: descriptionId }, typeof description === 'string' ? React__default.createElement(Paragraph, null, description) : description),
                        React__default.createElement(Box, { paddingBlockStart: "medium" },
                            React__default.createElement(Stack, { justifyContent: "end" }, children))))))));
};
/**
 * A dialog that interrupts the user with a mandatory confirmation or action.
 */
const AlertModal = (props) => {
    const parentId = useFloatingParentNodeId();
    // This is a root, so we wrap it with the tree
    if (parentId === null) {
        return (React__default.createElement(FloatingTree, null,
            React__default.createElement(AlertModalInner, { ...props })));
    }
    return React__default.createElement(AlertModalInner, { ...props });
};

export { AlertModal };
